import type { BiMetricConfig } from '@repo/api-codegen';
import type { LegendsInfo } from '../../utils/charts/bi/types';
import { CHART_OPACITY } from './constants';

// Type guards to determine chart type
export const isBarChartConfig = (
	config: BiMetricConfig['chart_config']
): config is BiMetricConfig['chart_config'] & { chartType: 'bar' } =>
	'chartType' in config && config.chartType === 'bar';

export const isLineChartConfig = (
	config: BiMetricConfig['chart_config']
): config is BiMetricConfig['chart_config'] & { chartType: 'line' } =>
	'chartType' in config && config.chartType === 'line';

export const isScatterChartConfig = (
	config: BiMetricConfig['chart_config']
): config is BiMetricConfig['chart_config'] & { chartType: 'scatter' } =>
	'chartType' in config && config.chartType === 'scatter';

export const isPieChartConfig = (
	config: BiMetricConfig['chart_config']
): config is BiMetricConfig['chart_config'] & { chartType: 'pie' } =>
	'chartType' in config && config.chartType === 'pie';

export const isNumberChartConfig = (
	config: BiMetricConfig['chart_config']
): config is BiMetricConfig['chart_config'] & { chartType: 'number' } =>
	'chartType' in config && config.chartType === 'number';

export const isTableChartConfig = (
	config: BiMetricConfig['chart_config']
): config is BiMetricConfig['chart_config'] & { chartType: 'table' } =>
	'chartType' in config && config.chartType === 'table';

export function formatTimeFrame(timeFrame: string): string {
	return timeFrame.replace(/(\d+)([a-zA-Z]+)/, '$1 $2');
}

// Attaches data-legend attributes to chart elements based on their fill/stroke colors
export function attachLegendAttributes(svg: SVGElement, legends: LegendsInfo) {
	Object.entries(legends).forEach(([legendKey, legendInfo]) => {
		const { color } = legendInfo;

		// For fill colors (bars, pie, scatter)
		const fillElements = svg.querySelectorAll(`[fill="${color}"]`);
		fillElements.forEach((element) =>
			element.setAttribute('data-legend', legendKey)
		);

		// For stroke colors (lines)
		const strokeElements = svg.querySelectorAll(`[stroke="${color}"]`);
		strokeElements.forEach((element) =>
			element.setAttribute('data-legend', legendKey)
		);
	});
}

// Updates chart element opacity based on hovered legend state
export function updateChartOpacityByLegend(
	svg: SVGElement,
	legends: LegendsInfo,
	hoveredLegendName: string | null
) {
	Object.keys(legends).forEach((legendKey) => {
		const opacity =
			hoveredLegendName === null || hoveredLegendName === legendKey
				? CHART_OPACITY.ACTIVE
				: CHART_OPACITY.INACTIVE;

		const elements = svg.querySelectorAll(`[data-legend="${legendKey}"]`);
		elements.forEach((element) => {
			if (element.hasAttribute('fill')) {
				element.setAttribute('fill-opacity', opacity);
			}
			if (element.hasAttribute('stroke')) {
				element.setAttribute('stroke-opacity', opacity);
			}
		});
	});
}

// Attaches hover event handlers to pie chart segment elements
export function attachPieChartHoverHandlers(
	svg: SVGElement,
	onLegendHover: (legendName: string | null) => void
) {
	const pieChartContainer = svg.querySelector('.pie-chart-container');
	if (!pieChartContainer) return null;

	const segmentElements = pieChartContainer.querySelectorAll('[data-legend]');

	const handleMouseEnter = (event: Event) => {
		const target = event.target as Element;
		const legendName = target.getAttribute('data-legend');
		if (legendName) {
			onLegendHover(legendName);
		}
	};

	const handleMouseLeave = () => {
		onLegendHover(null);
	};

	segmentElements.forEach((element) => {
		element.addEventListener('mouseenter', handleMouseEnter);
		element.addEventListener('mouseleave', handleMouseLeave);
	});

	return () => {
		segmentElements.forEach((element) => {
			element.removeEventListener('mouseenter', handleMouseEnter);
			element.removeEventListener('mouseleave', handleMouseLeave);
		});
	};
}
